import { Fragment, useEffect, useState } from 'react';
import { Empty, Skeleton } from '../components/ui';
import { api } from '../lib/api';
import { ago, dateTime } from '../lib/format';
import { t } from '../lib/i18n';

interface AuditItem {
  id: string;
  action: string;
  actor: { type: 'user' | 'api_key' | 'system' | 'staff'; id: string | null; label: string };
  target: { type: string | null; id: string | null } | null;
  ip: string | null;
  data: Record<string, unknown>;
  created_at: string;
}

const ACTIONS: Record<string, string> = {
  'auth.login': 'Вход',
  'auth.login_2fa': 'Вход с 2FA',
  'auth.login_recovery_code': 'Вход по коду восстановления',
  'auth.password_reset': 'Сброс пароля',
  'api_key.create': 'Создан API-ключ',
  'api_key.revoke': 'Отозван API-ключ',
  'domain.create': 'Добавлен домен',
  'domain.verify': 'Проверка DNS',
  'domain.delete': 'Удалён домен',
  'email.test_send': 'Тестовое письмо',
  'suppression.create': 'Адрес в подавлениях',
  'suppression.delete': 'Адрес убран из подавлений',
  'webhook.create': 'Создан вебхук',
  'webhook.delete': 'Удалён вебхук',
  'webhook.ping': 'Ping вебхука',
  'team.invite': 'Приглашение',
  'team.invite_revoke': 'Приглашение отозвано',
  'team.invite_accept': 'Приглашение принято',
  'team.role_change': 'Смена роли',
  'team.member_remove': 'Участник удалён',
  'template.create': 'Создан шаблон',
  'template.update': 'Новая версия шаблона',
  'template.delete': 'Удалён шаблон',
  'workspace.update': 'Настройки пространства',
  'workspace.create': 'Новое пространство',
  'onboarding.finish': 'Онбординг завершён',
  'security.2fa_enable': '2FA включён',
  'security.2fa_disable': '2FA выключен',
  'security.recovery_codes_regenerate': 'Новые коды восстановления',
  'security.passkey_add': 'Добавлен passkey',
  'security.passkey_remove': 'Удалён passkey',
  'auth.login_passkey': 'Вход с passkey',
  'audit.export': 'Экспорт журнала',
};
const tone = (a: string) => (/delete|revoke|remove|disable/.test(a) ? 'bad' : /security|role|password|recovery/.test(a) ? 'warn' : /create|enable|accept/.test(a) ? 'ok' : 'sent');

export function AuditPage() {
  const [items, setItems] = useState<AuditItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<string | null>(null);

  async function load(more = false) {
    setLoading(true);
    try {
      const res = await api.get<{ items: AuditItem[]; next_cursor: string | null }>(`/audit?limit=50${more && cursor ? `&cursor=${cursor}` : ''}`);
      setItems((xs) => (more ? [...xs, ...res.items] : res.items));
      setCursor(res.next_cursor);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className="page-head">
        <div className="grow">
          <h1>{t('Журнал действий')}</h1>
          <p>{t('Кто, что и когда изменил: люди и API-ключи. Пароли, токены и секреты сюда не попадают.')}</p>
        </div>
        <a className="btn" href="/dashboard/v1/audit/export" download>{t('Скачать CSV')}</a>
        <button className="btn" onClick={() => load()}>{t('Обновить')}</button>
      </div>
      <div className="card">
        {loading && !items.length ? (
          <div className="card-pad stack">{Array.from({ length: 6 }, (_, i) => <Skeleton key={i} />)}</div>
        ) : !items.length ? (
          <Empty title={t('Пока тихо')}>{t('Здесь появятся действия команды и API-ключей.')}</Empty>
        ) : (
          <table className="table">
            <thead><tr><th>{t('Когда')}</th><th>{t('Действие')}</th><th>{t('Кто')}</th><th>{t('Объект')}</th><th>IP</th></tr></thead>
            <tbody>
              {items.map((e) => (
                <Fragment key={e.id}>
                  <tr className="click" onClick={() => setOpen(open === e.id ? null : e.id)}>
                    <td className="small muted" title={dateTime(e.created_at)} style={{ whiteSpace: 'nowrap' }}>{ago(e.created_at)}</td>
                    <td><span className={`pill ${tone(e.action)}`}>{t(ACTIONS[e.action] ?? e.action)}</span></td>
                    <td className="truncate">
                      {e.actor.type === 'api_key' ? '🔑 ' : ''}
                      {e.actor.type === 'staff' && <span className="pill warn" style={{ marginRight: 6 }}>{t('Сотрудник MailOcean')}</span>}
                      {e.actor.label}
                    </td>
                    <td className="small"><code>{e.target?.id ?? '—'}</code></td>
                    <td className="small muted">{e.ip ?? '—'}</td>
                  </tr>
                  {open === e.id && (
                    <tr>
                      <td colSpan={5}><pre className="code">{JSON.stringify({ action: e.action, target: e.target, data: e.data }, null, 2)}</pre></td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
        {cursor && (
          <div className="card-pad" style={{ textAlign: 'center', borderTop: '1px solid var(--border)' }}>
            <button className="btn" onClick={() => load(true)} disabled={loading}>{t('Показать раньше')}</button>
          </div>
        )}
      </div>
    </>
  );
}
